/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-empty-object-type, @typescript-eslint/no-unsafe-function-type, @typescript-eslint/no-wrapper-object-types , complexity */

import { Amplify } from 'aws-amplify';
import {
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  confirmUserAttribute,
  fetchAuthSession,
  fetchMFAPreference,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  sendUserAttributeVerificationCode,
  setUpTOTP,
  signIn,
  signInWithRedirect,
  signOut,
  signUp,
  updateMFAPreference,
  updatePassword,
  updateUserAttributes,
  verifyTOTPSetup
} from 'aws-amplify/auth';

import { Logger } from '../../logger';
import { AWSCredentials } from '../../signer/aws/aws-credentials';
import { Authenticator } from '../authenticator';
import { AmplifyAuthConfig } from './types';
import { CurrentUserCheck } from '../auth0/types';

/**
 * Opaque user handle. v2 of Amplify exposed `CognitoUser` from
 * `amazon-cognito-identity-js`; consumers only used it as a typed placeholder
 * for the value returned from `signIn` / `getCurrentAuthenticatedUser` (no
 * methods are called on it). We keep the legacy name so call sites don't
 * change.
 */
export type CognitoUser = any;

/**
 * Mapping between v2 Cognito MFA identifiers (still used throughout the app)
 * and the new v6 short-form identifiers (`'SMS' | 'TOTP' | 'EMAIL'`).
 */
const V6_TO_V2_MFA: Record<string, string> = {
  SMS: 'SMS_MFA',
  TOTP: 'SOFTWARE_TOKEN_MFA',
  EMAIL: 'EMAIL_MFA'
};

/**
 * v2 Amplify was asymmetric: `Auth.setPreferredMFA(user, type)` accepted the
 * short preference names (`'SMS' | 'TOTP' | 'NOMFA'`) while
 * `Auth.getPreferredMFA(user)` returned challenge names (`'SMS_MFA' |
 * 'SOFTWARE_TOKEN_MFA' | 'NOMFA'`). Existing call sites pass either form, so
 * accept both.
 */
const V2_TO_V6_MFA: Record<string, 'SMS' | 'TOTP' | 'EMAIL' | 'NOMFA'> = {
  SMS: 'SMS',
  SMS_MFA: 'SMS',
  TOTP: 'TOTP',
  SOFTWARE_TOKEN_MFA: 'TOTP',
  EMAIL: 'EMAIL',
  EMAIL_MFA: 'EMAIL',
  NOMFA: 'NOMFA'
};

/**
 * Mapping between v6 `nextStep.signInStep` values and the legacy
 * `challengeName` strings still inspected by the login flow.
 */
const SIGN_IN_STEP_TO_CHALLENGE_NAME: Record<string, string> = {
  CONFIRM_SIGN_IN_WITH_SMS_CODE: 'SMS_MFA',
  CONFIRM_SIGN_IN_WITH_TOTP_CODE: 'SOFTWARE_TOKEN_MFA',
  CONFIRM_SIGN_IN_WITH_EMAIL_CODE: 'EMAIL_MFA',
  CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED: 'NEW_PASSWORD_REQUIRED',
  CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE: 'CUSTOM_CHALLENGE'
};

/**
 * Translates the legacy `AmplifyAuthConfig` shape used throughout the app
 * (which mirrors the v2 `Amplify.Auth.configure()` payload) into the nested
 * v6 `ResourcesConfig` shape required by `Amplify.configure()`.
 */
const toV6Config = (config: AmplifyAuthConfig): Parameters<typeof Amplify.configure>[0] => {
  const { Auth } = config;
  const cookieStorage = Auth.cookieStorage
    ? {
        domain: Auth.cookieStorage.domain,
        path: Auth.cookieStorage.path,
        secure: Auth.cookieStorage.secure,
        expires: 365,
        sameSite: 'lax' as const
      }
    : undefined;

  return {
    Auth: {
      Cognito: {
        identityPoolId: Auth.identityPoolId,
        userPoolId: Auth.userPoolId as string,
        userPoolClientId: Auth.userPoolWebClientId as string,
        loginWith: {
          oauth: {
            domain: Auth.oauth.domain,
            scopes: Auth.oauth.scope,
            redirectSignIn: [Auth.oauth.redirectSignIn],
            redirectSignOut: [Auth.oauth.redirectSignOut],
            responseType: Auth.oauth.responseType as 'code' | 'token'
          }
        }
      }
    },
    ...(cookieStorage && { ssr: false })
  };
};

/**
 * Authenticator wrapper for Amplify Auth.
 *
 * Adapts the v6 `aws-amplify/auth` functional API to the legacy method
 * signatures the rest of the codebase relies on. All `user` parameters are
 * accepted for backwards compatibility but ignored — v6 stores the session
 * internally.
 *
 * @see {@link https://docs.amplify.aws/javascript/build-a-backend/auth/ | Amplify v6 Auth docs}
 */
export class AmplifyAuthenticator extends Authenticator<AmplifyAuthConfig> {
  private _expiry: Date | null = null;
  private _secretAccessKey: string | null = null;
  private _accessKeyId: string | null = null;
  private _sessionToken: string | null = null;

  constructor(config: AmplifyAuthConfig) {
    super(config);
    Amplify.configure(toV6Config(config));
  }

  /**
   * Requests new credentials from the server.
   * @returns {Promise<AWSCredentials>}
   * @throws if the credentials can be got
   */
  public async fetchCredentials(): Promise<AWSCredentials | never> {
    return this.fetchEssentialCredentials();
  }

  /**
   * Checks if the credentials have been expired.
   * @returns {Promise<boolean>}
   * @throws if the credentials can be got
   */
  public async hasExpiredCredentials(): Promise<boolean | never> {
    await this.fetchEssentialCredentials();
    return this._expiry === null || this._expiry.getTime() < Date.now();
  }

  /**
   * Returns a user object shaped like the legacy v2 `currentAuthenticatedUser`
   * response so existing consumers (which read `user.attributes.*`,
   * `user.username` and `user.preferredMFA`) keep working unchanged.
   */
  public async getCurrentAuthenticatedUser(_currentUser?: CurrentUserCheck): Promise<any> {
    const [current, attributesObject, mfaPreference] = await Promise.all([
      getCurrentUser(),
      fetchUserAttributes().catch(() => ({} as Record<string, string | undefined>)),
      fetchMFAPreference().catch(() => ({ preferred: undefined } as { preferred?: 'SMS' | 'TOTP' | 'EMAIL' }))
    ]);

    return {
      username: current.username,
      userId: current.userId,
      attributes: attributesObject,
      preferredMFA: mfaPreference.preferred ? V6_TO_V2_MFA[mfaPreference.preferred] : 'NOMFA'
    };
  }

  /**
   * Returns the user attributes in the legacy `Array<{ Name, Value }>` shape
   * (what the v2 `Amplify.Auth.userAttributes()` returned) so that the
   * `formatUserAttributes` reducer keeps working unchanged.
   */
  public async getUserAttributes(_user: any): Promise<any> {
    const attributesObject = await fetchUserAttributes();
    return Object.entries(attributesObject).map(([Name, Value]) => ({ Name, Value }));
  }

  public async updateUserAttributes(_user: any, attributes: Record<string, string>): Promise<any> {
    return updateUserAttributes({ userAttributes: attributes });
  }

  public async changePassword(_user: any, oldPassword: string, newPassword: string): Promise<any> {
    return updatePassword({ oldPassword, newPassword });
  }

  /**
   * NEW_PASSWORD_REQUIRED is now driven by `confirmSignIn` with the new
   * password as the challenge response (see v6 nextStep documentation).
   */
  public async completeNewPassword(_user: any, password: string, _requiredAttributes: any = {}): Promise<any> {
    return confirmSignIn({ challengeResponse: password });
  }

  public async forgotPassword(username: string): Promise<any> {
    return resetPassword({ username });
  }

  public async forgotPasswordSubmit(username: string, code: string, password: string): Promise<any> {
    return confirmResetPassword({ username, confirmationCode: code, newPassword: password });
  }

  /**
   * v6 `signIn` returns `{ isSignedIn, nextStep }` rather than a user object.
   * We synthesize a `challengeName` field on the result so the existing login
   * action (`signInStarter`) can continue to detect SMS MFA without changes.
   *
   * v6 also throws `UserAlreadyAuthenticatedException` if a session already
   * exists (v2 silently replaced it). Sign any pre-existing user out first so
   * the legacy "log in over the top" flow keeps working.
   */
  public async signIn(username?: string, password?: string): Promise<any> {
    try {
      await getCurrentUser();
      await signOut();
    } catch {
      // No existing session — nothing to clear.
    }

    const result = await signIn({ username: username || '', password });
    const challengeName = result.nextStep ? SIGN_IN_STEP_TO_CHALLENGE_NAME[result.nextStep.signInStep] : undefined;

    return { ...result, challengeName };
  }

  public async signOut(): Promise<any> {
    try {
      await signOut();
    } catch (error) {
      Logger.error(String(error));
    }

    this.recordSessionEnded();
    this.invalidateCurrentSession();
  }

  /**
   * v6 `confirmSignIn` doesn't take a user or an MFA type — the SDK tracks
   * the in-flight challenge internally.
   */
  public async confirmSignIn(_user: any, code: string, _mfaType: string | null): Promise<any> {
    return confirmSignIn({ challengeResponse: code });
  }

  public async getPreferredMFA(_user: any): Promise<string> {
    const preference = await fetchMFAPreference();
    return preference.preferred ? V6_TO_V2_MFA[preference.preferred] : 'NOMFA';
  }

  public async setPreferredMFA(_user: any, mfaMethod: string): Promise<any> {
    const v6Mfa = V2_TO_V6_MFA[mfaMethod] ?? 'NOMFA';
    if (v6Mfa === 'NOMFA') {
      return updateMFAPreference({ sms: 'DISABLED', totp: 'DISABLED', email: 'DISABLED' });
    }
    const key = v6Mfa === 'SMS' ? 'sms' : v6Mfa === 'TOTP' ? 'totp' : 'email';
    return updateMFAPreference({ [key]: 'PREFERRED' });
  }

  public async setupTOTP(_user: any): Promise<{}> {
    return setUpTOTP();
  }

  public async verifyTotpToken(_user: any, challengeAnswer: any): Promise<{}> {
    await verifyTOTPSetup({ code: String(challengeAnswer) });
    return {};
  }

  public async verifyCurrentUserAttributeSubmit(attribute: string, code: string): Promise<string> {
    await confirmUserAttribute({ userAttributeKey: attribute as any, confirmationCode: code });
    return 'SUCCESS';
  }

  public async verifyUserAttribute(_user: any, attrs: string): Promise<{}> {
    return sendUserAttributeVerificationCode({ userAttributeKey: attrs as any });
  }

  /**
   * Accepts the legacy positional signature `(username, password, email, …)`
   * used by the v2 wrapper as well as a v6 options object.
   */
  public async signUp(data: string | object, ...restOfAttrs: string[]): Promise<any> {
    if (typeof data === 'object') {
      return signUp(data as any);
    }
    const [password, email] = restOfAttrs;
    return signUp({
      username: data,
      password,
      options: email ? { userAttributes: { email } } : undefined
    });
  }

  public async confirmSignUp(username: string, code: string, _options?: any): Promise<any> {
    return confirmSignUp({ username, confirmationCode: code });
  }

  /**
   * Maps the legacy `{ customProvider, provider }` options shape onto v6's
   * `signInWithRedirect` provider argument.
   */
  public async federatedSignIn(options?: any): Promise<any> {
    if (!options) {
      return signInWithRedirect();
    }
    if (options.customProvider) {
      return signInWithRedirect({ provider: { custom: options.customProvider } });
    }
    if (options.provider) {
      return signInWithRedirect({ provider: options.provider });
    }
    return signInWithRedirect(options);
  }

  public async resendSignUp(username: string): Promise<any> {
    return resendSignUpCode({ username });
  }

  public async handleRedirectCallback(): Promise<void> {
    throw new Error('handleRedirectCallback is not supported for Amplify authenticator.');
  }

  public updateCurrentUser(): void {
    throw new Error('updateCurrentUser is not supported for Amplify authenticator.');
  }

  /**
   * Gets the essential credentials.
   *
   * v6 collapses `currentSession` + `currentCredentials` + `essentialCredentials`
   * into a single `fetchAuthSession({ forceRefresh })` call.
   *
   * @returns {Promise<AWSCredentials>}
   * @throws {Error} if the credentials or jwtToken cannot be gotten
   * @private
   */
  private async fetchEssentialCredentials(): Promise<AWSCredentials | never> {
    const session = await fetchAuthSession({ forceRefresh: this.requestNewCredentials() });
    const jwtToken = session.tokens?.idToken?.toString();

    if (!jwtToken) {
      throw new Error('There is no user session or the jwtToken is not set');
    }

    if (this.requestNewCredentials()) {
      const credentials = session.credentials;
      if (!credentials) {
        throw new Error('There are no credentials');
      }

      this._expiry = credentials.expiration ?? new Date(0);
      this._secretAccessKey = credentials.secretAccessKey;
      this._accessKeyId = credentials.accessKeyId;
      this._sessionToken = credentials.sessionToken ?? null;
    }

    if (!this._secretAccessKey || !this._accessKeyId || !this._sessionToken) {
      throw new Error('There are no credentials');
    }

    this.recordSessionActivity();
    return new AWSCredentials(this._secretAccessKey, this._accessKeyId, this._sessionToken, jwtToken);
  }

  /**
   * Determine whether it is needed to request new credentials or not.
   * @returns {boolean}
   */
  private requestNewCredentials(): boolean {
    return this._expiry === null || this._expiry.getTime() < Date.now();
  }

  /**
   * Invalidates the current session setting it to null.
   */
  private invalidateCurrentSession(): void {
    this._expiry = null;
    this._secretAccessKey = null;
    this._accessKeyId = null;
    this._sessionToken = null;
  }
}
